/* eslint-disable @nx/enforce-module-boundaries */
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Link,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import { UIShell } from '@gamma/ui/layout';
import { ReactNode, useContext, useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import {
  IGetUserCookieAcceptance,
  useGetUserCookieAcceptance,
} from '@investigator/features/user/queries';
import { getIsCookieExpired } from '@investigator/shared/utils';
import { ROUTES } from '../../constants';
import { useLogOperationCall, useUserSession } from '../../hooks';
import { i18n } from '../../localization';
import { AuthContext, CookieContext } from '../../providers';
import { AuthState, CognitoUserInterface } from '../../types/auth-types';

import { Navbar } from '@gamma/ui/navigation';
import { safeParse } from 'secure-json-parse';
import { CookiePolicy } from '@investigator/features/user/components/CookiePolicy';
import { SidebarFooterLinks, SidebarLinks, TopbarLinks } from './Components';
import { Investigator } from '@gamma/ui/logos';

const {
  chatGPTToastDescriptionMessage,
  chatGPTToastTitleMessage,
  gptSettings,
} = i18n.pages.integrations.chatGPT;

const { login, home, systemSettingsIntegrationsConfig } = ROUTES;

interface WrapperProps {
  children: ReactNode;
  user: CognitoUserInterface | undefined;
}

export const Wrapper = ({ children, user }: WrapperProps) => {
  const {
    userLicense,
    userLicenseLoading,
    userRole,
    userPrivileges,
    userPrivilegesLoading,
    authState,
  } = useContext(AuthContext);

  const { checkUserMFA } = useUserSession();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const logOperationCall = useLogOperationCall();

  // @ts-expect-error local storage is currently not typed
  const isSSOLogin = safeParse(window.sessionStorage.getItem('isSSOLogin'));

  const { setCookieConfig, getUserCookieAcceptance, isLoaded } =
    useContext(CookieContext);

  const showToastMessage = useToast();

  const navigate = useNavigate();

  const handleButtonClick = (route?: string) => {
    localStorage.setItem('shouldShowGPTToast', 'false');
    localStorage.removeItem('shouldShowGPTToastGeneralSettingsLink');
    showGPTToast?.closeAll();

    if (route) {
      window.open(route, '_self');
    }
  };

  const gptToastID = 'show_gpt_toast';
  const showGPTToast = useToast({
    position: 'bottom-right',
    isClosable: false,
    status: 'warning',
    duration: null,
    id: gptToastID,
    title: chatGPTToastTitleMessage,
    description: (
      <Flex mr={1} flexDir="column" alignItems="flex-start">
        <Text textStyle="body-md">{chatGPTToastDescriptionMessage}</Text>
        {/* need local storage here b/c some renders (after refreshing the page) isAdmin is falsy */}
        {(localStorage.getItem('shouldShowGPTToastGeneralSettingsLink') ===
          'true' ||
          userRole === 'admin') && (
          <Button
            onClick={() => handleButtonClick(systemSettingsIntegrationsConfig)}
            variant="link"
          >
            {gptSettings}
          </Button>
        )}
        <Button
          data-testid="gpt-toast-close-button"
          position="absolute"
          top="5px"
          right="5px"
          onClick={() => handleButtonClick()}
        >
          <CloseSharpIcon />
        </Button>
      </Flex>
    ),
  });

  // Refetch cookie acceptance if there is an error (for test reliability)
  const {
    error: getUserCookieAcceptanceError,
    refetch: refetchUserCookieAcceptance,
  } = useGetUserCookieAcceptance({
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (getUserCookieAcceptanceError && isSSOLogin) {
      refetchUserCookieAcceptance();
    }
  }, [getUserCookieAcceptanceError, isSSOLogin, refetchUserCookieAcceptance]);

  const { data: _getUserCookieAcceptanceData } = useGetUserCookieAcceptance({
    onCompleted: (data: IGetUserCookieAcceptance) => {
      const { isCookiePolicyExpired } = getIsCookieExpired(
        data?.getUserCookieAcceptance,
      );
      setCookieConfig?.((prev) => ({
        ...prev,
        ...data,
        initialStep: 1,
        isOpen: isCookiePolicyExpired,
        isLoaded: true,
      }));
    },
    onError: () => {
      const { isCookiePolicyExpired } = getIsCookieExpired(
        getUserCookieAcceptance,
      );
      setCookieConfig?.((prev) => ({
        ...prev,
        isOpen: isCookiePolicyExpired,
        isLoaded: true,
      }));
    },
    fetchPolicy: 'no-cache',
  });

  const getUserTcAcceptanceLoading = !isLoaded;

  useEffect(() => {
    if (!isSSOLogin) {
      checkUserMFA(() =>
        // navigate to MFA registration without hard reload
        navigate('/registration/mfa', {
          replace: true,
          state: { routeAccessAllowed: true },
        }),
      );
    }
  }, [user, isSSOLogin, checkUserMFA, navigate]);

  const routerLocation = useLocation();

  const hasProcessedCookieAcceptance = useRef(false);

  useEffect(() => {
    const { location } = window;

    if (
      location.href.includes('analytics=true') ||
      location.href.includes('analytics=false')
    ) {
      const toastClick = () => navigate(ROUTES.accountSettings);

      showToastMessage({
        status: 'success',
        title: i18n.cookiePolicy.toastMessages.success.title,
        description: (
          <Box maxW="400px">
            {i18n.cookiePolicy.toastMessages.success.description}
            <Button variant="link" size="xs" onClick={toastClick}>
              {i18n.cookiePolicy.toastMessages.success.link}
            </Button>
          </Box>
        ),
        isClosable: true,
        position: 'bottom-right',
      });
      const urlSearchParams = new URLSearchParams(location.search);
      const cookieStatusInput =
        urlSearchParams.get('analytics') === 'true'
          ? { cookie_status: 'enabled' }
          : urlSearchParams.get('analytics') === 'false'
            ? { cookie_status: 'disabled' }
            : null;

      if (cookieStatusInput?.cookie_status) {
        logOperationCall(
          'COOKIE_UPDATE',
          'USER',
          JSON.stringify(cookieStatusInput),
        );
      }

      const newLocationSearch = location.search.replace(
        /[&?]analytics=(true|false)/,
        '',
      );

      navigate(location.pathname + newLocationSearch);
    }

    if (hasProcessedCookieAcceptance.current) return;

    const { isCookiePolicyExpired } = getIsCookieExpired(
      getUserCookieAcceptance,
    );

    if (isCookiePolicyExpired) {
      setCookieConfig?.((prev) => ({ ...prev, isOpen: true }));
    }

    hasProcessedCookieAcceptance.current = true;
  }, [
    routerLocation.pathname,
    getUserCookieAcceptance,
    showToastMessage,
    navigate,
    logOperationCall,
    setCookieConfig,
  ]);

  useEffect(() => {
    const pathsThatShouldEnableShowingGPTToastOnFirstVisit = [
      'detections',
      'alert-catalog',
    ];

    const isOnPathWithGPTContent =
      pathsThatShouldEnableShowingGPTToastOnFirstVisit.some((path) =>
        routerLocation.pathname.includes(path),
      );

    // set toast id to be used later so we can prevent duplicate toasts from showing
    if (
      isOnPathWithGPTContent &&
      localStorage.getItem('shouldShowGPTToast') !== 'false'
    ) {
      localStorage.setItem('shouldShowGPTToast', 'true');
    }

    // prevent duplicate toasts from showing with "!showGPTToast?.isActive(gptToastID)"
    if (
      localStorage.getItem('shouldShowGPTToast') === 'true' &&
      !showGPTToast?.isActive(gptToastID)
    ) {
      // persist toast link to general settings page if user is admin
      if (userRole === 'admin') {
        localStorage.setItem('shouldShowGPTToastGeneralSettingsLink', 'true');
      }
      showGPTToast();
    }

    if (!user) {
      showGPTToast.close(gptToastID);
    }
  }, [showGPTToast, routerLocation.pathname, user, userLicense, userRole]);

  if (authState === AuthState.SignOut) {
    return <Navigate to={login} replace />;
  }

  if (authState !== AuthState.Loading) {
    if (
      (user?.preferredMFA === 'NOMFA' &&
        !user?.attributes?.['custom:allow_no_mfa'] &&
        !isSSOLogin) ||
      (!userLicense && userLicenseLoading) ||
      (!userPrivileges && userPrivilegesLoading) ||
      getUserTcAcceptanceLoading
    ) {
      return (
        <Center bg={bgColor} h="100%">
          <Spinner size="lg" />
        </Center>
      );
    }

    return (
      <>
        <UIShell
          sidebar={<SidebarLinks />}
          sidebarFooter={<SidebarFooterLinks />}
          nav={
            <Navbar>
              <TopbarLinks />
            </Navbar>
          }
          logo={
            <HStack>
              <Link href={home} _focus={{ outline: 'none' }}>
                <Investigator data-testid="investigator-logo" height="20px" />
              </Link>
              {userLicense?.display_name && (
                <>
                  <Text mr={0.5} as="span" fontWeight="500">
                    —
                  </Text>
                  <Text as="span" fontWeight="500">
                    {userLicense?.display_name}
                  </Text>
                </>
              )}
            </HStack>
          }
        >
          {children}
        </UIShell>
        {!getUserTcAcceptanceLoading && <CookiePolicy />}
      </>
    );
  } else {
    return (
      <Center h="100%">
        <Spinner color="corelight.base" size="lg" />
      </Center>
    );
  }
};
